<template>
    <div class="fixed inset-0 bg-black/[0.04] flex items-center justify-center z-[1000] backdrop-blur-sm" 
         @click.self="handleBackdropClick">
        <div class="relative bg-n-alpha-3 shadow-xl rounded-xl skip-context-menu w-[45rem] max-h-[90vh] flex flex-col overflow-hidden">
            <div class="sticky top-0 flex justify-between items-center p-4 border-b-2 border-n-slate-4 dark:border-n-slate-7 bg-n-alpha-3 rounded-t-xl z-10">
                <h3 class="text-lg font-semibold m-0">{{ modalTitle }}</h3>
                <button
                    class="z-10 ltr:right-2 rtl:left-2 top-2 inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent h-10 w-10 p-0 text-sm font-medium justify-center"
                    @click="closeModal">
                    <span class="i-lucide-x flex-shrink-0"></span>
                </button>
            </div>

            <div class="p-6 overflow-auto flex-1">
                <!-- Form fields for add/edit modes -->
                <template v-if="mode === 'add' || mode === 'edt'">
                    <!-- Professional Name and Color Row -->
                    <div class="mb-1 flex gap-3">
                        <div class="flex-1">
                            <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Nome do profissional <span class="text-red-500">*</span></label>
                            <input 
                                type="text" 
                                v-model="formData.name" 
                                placeholder="Insira o nome do profissional"
                                class="w-full px-3 py-2.5 border border-n-slate-3 dark:border-n-slate-6 !rounded-[8px] text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1"
                                maxlength="255"
                                required 
                            />
                        </div>
                        
                        <!-- Color Dropdown -->
                        <div class="w-20">
                            <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Cor</label>
                            <div class="relative">
                                <button
                                    type="button"
                                    class="w-full h-[42px] px-3 border border-n-slate-7 dark:border-n-slate-6 !rounded-[8px] bg-n-alpha-white text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1 flex items-center justify-between text-sm"
                                    @click="toggleColorDropdown"
                                    ref="colorButton"
                                >
                                    <div 
                                        class="w-7 h-7 rounded-full border border-n-slate-5 p-0"
                                        :style="{ backgroundColor: selectedColor.value }"
                                    ></div>
                                    <span class="i-lucide-chevron-down flex-shrink-0 transition-transform" :class="{ 'rotate-180': showColorDropdown }"></span>
                                </button>
                                
                                <Teleport to="body">
                                    <div
                                        v-if="showColorDropdown"
                                        v-on-clickaway="closeColorDropdown"
                                        :style="colorDropdownStyle"
                                        class="dropdown-pane dropdown-pane--open fixed z-[1001] bg-white dark:bg-n-slate-2 shadow-lg rounded-lg border border-n-slate-3 dark:border-n-slate-6 p-2"
                                    >
                                        <div class="grid grid-cols-2 gap-1">
                                            <div
                                                v-for="color in predefinedColors"
                                                :key="color.colorId"
                                                class="flex items-center justify-center"
                                            >
                                                <button
                                                    type="button"
                                                    @click="selectColorFromDropdown(color)"
                                                    class="w-8 h-8 rounded-full border-2 transition-all duration-150 hover:scale-110 border-n-slate-4 hover:border-n-slate-7"
                                                    :style="{ backgroundColor: color.value }"
                                                    :title="color.name"
                                                ></button>
                                            </div>
                                        </div>
                                    </div>
                                </Teleport>
                            </div>
                        </div>
                    </div>

                    <!-- Email -->
                    <div class="mb-5">
                        <label class="block mb-1.5 text-sm font-medium text-n-slate-12">E-mail</label>
                        <input 
                            type="email" 
                            v-model="formData.email" 
                            placeholder="email@exemplo.com"
                            :class="[
                                'w-full px-3 py-2.5 border !rounded-[8px] text-sm bg-n-background text-n-slate-12 hover:bg-n-slate-1 !mb-2',
                                formData.email && !isEmailValid 
                                    ? 'border-red-500 focus:border-red-500' 
                                    : 'border-n-slate-3 dark:border-n-slate-6 focus:border-n-brand'
                            ]"
                            maxlength="255"
                        />
                        <p v-if="formData.email && !isEmailValid" class="text-xs text-red-500">E-mail inválido</p>
                        <p v-else class="text-xs text-n-slate-11">Iremos utilizar esse endereço email para convidar o profissional na agenda</p>
                    </div>

                    <!-- WhatsApp Phone -->
                    <div class="mb-5">
                        <label class="block mb-1.5 text-sm font-medium text-n-slate-12">WhatsApp</label>
                        <input 
                            type="text" 
                            v-model="maskedPhone" 
                            @input="handlePhoneInput"
                            @keypress="onlyNumbers"
                            placeholder="+55 99 99999-9999"
                            class="w-full px-3 py-2.5 border border-n-slate-3 dark:border-n-slate-6 !rounded-[8px] text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1 !mb-2"
                            maxlength="19"
                        />
                        <p class="text-xs text-n-slate-11">Iremos utilizar esse número para notificar o profissional sobre agendamentos</p>
                    </div>

                    <!-- Specialty Selection -->
                    <div class="mb-5">
                            <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Selecione especialidades <span class="text-red-500">*</span></label>
                            <div class="relative">
                                <button
                                    type="button"
                                    :disabled="!specialties || specialties.length === 0"
                                    class="w-full h-[40px] px-3 border border-n-slate-7 dark:border-n-slate-6 !rounded-[8px] bg-n-alpha-white text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1 text-left flex items-center justify-between text-base"
                                    @click="toggleSpecialtyDropdown"
                                    ref="specialtyButton"
                                >
                                    <span class="truncate">{{ getSelectedSpecialtiesText() }}</span>
                                    <span class="i-lucide-chevron-down flex-shrink-0 transition-transform" :class="{ 'rotate-180': showSpecialtyDropdown }"></span>
                                </button>
                                
                                <Teleport to="body">
                                    <div
                                        v-if="showSpecialtyDropdown"
                                        v-on-clickaway="closeSpecialtyDropdown"
                                        :style="dropdownStyle"
                                        class="dropdown-pane dropdown-pane--open fixed z-[1001] bg-white dark:bg-n-slate-2 shadow-lg rounded-lg border border-n-slate-3 dark:border-n-slate-6"
                                    >
                                        <div class="max-h-60 overflow-y-auto">
                                            <div v-for="specialty in (specialties || [])" :key="specialty.id"
                                                 class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
                                                 @click.stop="toggleSpecialty(specialty.id)">
                                                <div class="flex items-center">
                                                    <input 
                                                        type="checkbox"
                                                        :checked="selectedSpecialties.includes(specialty.id)"
                                                        class="w-4 h-4 text-n-brand bg-n-background border-n-slate-4 rounded focus:ring-n-brand dark:focus:ring-n-brand dark:ring-offset-slate-800 focus:ring-2 dark:bg-slate-700 dark:border-slate-600 pointer-events-none mr-3"
                                                        @click.stop
                                                    />
                                                    <div class="text-sm font-medium text-n-slate-12">
                                                        {{ specialty.name }}
                                                    </div>
                                                </div>
                                            </div>
                                            <div v-if="!specialties || specialties.length === 0" class="p-3 text-sm text-n-slate-10">
                                                Nenhuma especialidade disponível
                                            </div>
                                        </div>
                                    </div>
                                </Teleport>
                            </div>
                    </div>

                    <!-- Additional Information Section -->
                    <div>
                        <label class="block text-sm font-medium text-n-slate-12 mb-1.5">Informações Adicionais</label>
                        <textarea 
                            v-model="customField" 
                            placeholder="Digite informações adicionais sobre o profissional..."
                            rows="4"
                            maxlength="500"
                            class="w-full px-3 py-2.5 !rounded-[8px] text-sm !bg-n-alpha-3 dark:!bg-n-slate-1 resize-y min-h-[4rem] max-h-48 border border-n-slate-3 dark:border-n-slate-6 focus:border-n-brand"
                        ></textarea>
                        <div class="flex justify-end mt-1">
                            <span class="text-xs text-n-slate-10">{{ customField.length }}/500</span>
                        </div>
                    </div>

                    <!-- Working Hours Section (for add and edit modes) -->
                    <template v-if="mode === 'add' || mode === 'edt'">
                        <div class="mb-5">
                            <label class="block text-sm font-medium text-n-slate-12">Horários de Trabalho</label>
                            
                            <!-- Working Hours Fields -->
                            <transition-group
                                name="slide"
                                tag="div"
                                @before-enter="beforeEnter"
                                @enter="enter"
                                @after-enter="afterEnter"
                                @before-leave="beforeLeave"
                                @leave="leave"
                                @after-leave="afterLeave"
                                class="space-y-4 mb-3 overflow-hidden"
                            >
                                <div v-if="showWorkingHours" key="working-hours-content" 
                                     class="relative bg-n-slate-2 dark:bg-n-slate-12/5 p-4 rounded-lg border border-n-slate-5 dark:border-none mt-2">
                                    <!-- Days of Week Schedule -->
                                    <div class="space-y-4">
                                        <div v-for="day in daysOfWeek" :key="day.key" class="flex items-start gap-4 py-2">
                                            <!-- Day Toggle -->
                                            <div class="flex items-start gap-4 py-2">
                                                <label class="relative inline-flex items-center cursor-pointer">
                                                    <input v-model="workingHours[day.key].enabled" @change="ensureTimeSlots(day.key)" type="checkbox" class="sr-only peer" />
                                                    <div
                                                        class="relative w-11 h-6 bg-n-slate-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-n-brand/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-n-brand">
                                                    </div>
                                                </label>

                                                <!-- Day Name -->
                                                <span class="font-medium text-n-slate-12 min-w-[120px]">
                                                    {{ day.label }}
                                                </span>
                                            </div>

                                            <!-- Time Slots for enabled days -->
                                            <div v-if="workingHours[day.key].enabled" class="flex-1 flex gap-3 items-center">
                                                <!-- Time Slots Container -->
                                                <div class="flex flex-col gap-2">
                                                    <div v-for="(slot, slotIndex) in workingHours[day.key].timeSlots" :key="slotIndex">
                                                        <div class="flex items-center gap-2">
                                                            <!-- Start Time -->
                                                            <input v-model="slot.startTime" type="time"
                                                                @change="validateTimeSlot(day.key, slotIndex)"
                                                                class="px-3 py-2 border border-n-slate-3 dark:border-n-slate-6 rounded-md focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent bg-n-background text-n-slate-12 hover:bg-n-slate-1"
                                                                :class="{ 'border-n-ruby-6': workingHoursErrors[day.key]?.[slotIndex]?.startTime }" />

                                                            <!-- Separator -->
                                                            <span class="text-n-slate-11 font-medium">-</span>

                                                            <!-- End Time -->
                                                            <input v-model="slot.endTime" type="time"
                                                                @change="validateTimeSlot(day.key, slotIndex)"
                                                                class="px-3 py-2 border border-n-slate-3 dark:border-n-slate-6 rounded-md focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent bg-n-background text-n-slate-12 hover:bg-n-slate-1"
                                                                :class="{ 'border-n-ruby-6': workingHoursErrors[day.key]?.[slotIndex]?.endTime }" />

                                                            <!-- Action Buttons (only on first slot) -->
                                                            <div v-if="slotIndex === 0" class="flex items-start gap-2">
                                                                <!-- Add Time Slot Button -->
                                                                <Button icon="i-lucide-plus" variant="ghost" size="sm" @click="addTimeSlot(day.key)"
                                                                    v-tooltip.top="'Adicionar Horário'" />

                                                                <!-- Duplicate to Other Days Button -->
                                                                <Button icon="i-lucide-copy" variant="ghost" size="sm" v-tooltip.top="'Duplicar para outros dias'"
                                                                    @click="duplicateToOtherDays(day.key)" />
                                                            </div>

                                                            <!-- Remove Time Slot Button (only for additional slots) -->
                                                            <Button v-if="slotIndex > 0" icon="i-lucide-trash-2"
                                                                variant="ghost" color="ruby" size="sm" @click="removeTimeSlot(day.key, slotIndex)"
                                                                v-tooltip.top="'Apagar'" />
                                                        </div>

                                                        <!-- Error Messages for this slot -->
                                                        <div v-if="workingHoursErrors[day.key]?.[slotIndex]" class="pl-2 mt-1">
                                                            <p v-if="workingHoursErrors[day.key][slotIndex].startTime" class="text-sm text-n-ruby-9">
                                                                {{ workingHoursErrors[day.key][slotIndex].startTime }}
                                                            </p>
                                                            <p v-if="workingHoursErrors[day.key][slotIndex].endTime" class="text-sm text-n-ruby-9">
                                                                {{ workingHoursErrors[day.key][slotIndex].endTime }}
                                                            </p>
                                                            <p v-if="workingHoursErrors[day.key][slotIndex].overlap" class="text-sm text-n-ruby-9">
                                                                {{ workingHoursErrors[day.key][slotIndex].overlap }}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- Disabled Day Message -->
                                            <span v-else class="text-n-slate-9 text-sm italic pt-2">
                                                Dia desabilitado
                                            </span>
                                        </div>
                                    </div>

                                    <!-- Working Hours Summary -->
                                    <div class="border-t border-n-weak pt-4 mt-6">
                                        <div class="text-sm">
                                            <div class="flex items-center gap-2">
                                                <span class="text-n-slate-11">Dias ativos:</span>
                                                <span class="font-medium text-n-slate-12">
                                                    {{ Object.values(workingHours).filter(day => day.enabled).length }} de 7 dias
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </transition-group>

                            <!-- Add Working Hours Button -->
                            <button
                                type="button"
                                @click="toggleWorkingHours"
                                :class="{
                                    'flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-dashed w-full justify-center transition-colors': true,
                                    'text-n-slate-10 hover:text-n-brand hover:bg-n-slate-1 border-n-slate-4 dark:border-n-slate-6': true
                                }"
                            >
                                <span :class="showWorkingHours ? 'i-lucide-minus' : 'i-lucide-plus'" class="flex-shrink-0"></span>
                                <span>{{ showWorkingHours ? 'Remover horários de trabalho' : 'Adicionar horários de trabalho' }}</span>
                            </button>
                        </div>
                    </template>

                    <!-- Error Message -->
                    <div v-if="error" class="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <p class="text-red-600 dark:text-red-400 text-sm">{{ error }}</p>
                    </div>
                </template>

                <!-- Delete confirmation -->
                <template v-if="mode === 'del'">
                    <div class="flex gap-4 items-start">
                        <div class="flex-1">
                            <p class="text-n-slate-12 text-sm leading-relaxed">
                                Tem certeza que deseja excluir o profissional "<span class="font-medium">{{ professional.name }}</span>"? 
                                <span class="block mt-2 text-n-slate-10">Esta ação não pode ser desfeita.</span>
                            </p>
                        </div>
                    </div>
                    
                    <!-- Error Message for delete -->
                    <div v-if="error" class="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <p class="text-red-600 dark:text-red-400 text-sm">{{ error }}</p>
                    </div>
                </template>
            </div>

            <div class="flex justify-end gap-3 p-4 border-t-2 border-n-slate-4 dark:border-n-slate-7 sticky bottom-0 bg-n-alpha-3 rounded-b-xl">
                <button type="button"
                    class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                    @click="closeModal" :disabled="loading">
                    Cancelar
                </button>
                <button type="button"
                    :class="actionButtonClass"
                    @click="handleAction" :disabled="actionButtonDisabled">
                    <span v-if="loading" class="animate-spin i-lucide-loader"></span>
                    <span>{{ actionButtonText }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount, toRefs } from 'vue';
import { debounce } from 'lodash';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import wootAPI from '@/v3/api/apiClient';

export default {
    name: 'GeneralProfessionalModal',
    components: {
        WootDropdownMenu,
        WootDropdownItem,
        Button
    },
    props: {
        mode: {
            type: String,
            required: true,
            validator: (value) => ['add', 'edt', 'del'].includes(value)
        },
        professional: {
            type: Object,
            default: () => null
        },
        specialties: {
            type: Array,
            default: () => []
        }
    },
    emits: ['close', 'save', 'update', 'delete'],
    setup(props, { emit }) {
        const { specialties } = toRefs(props);
        
        const formData = ref({
            name: '',
            email: '',
            whatsapp: '',
            category: [],
            gender: ''
        });
        const selectedSpecialties = ref([]);
        const customField = ref('');
        const loading = ref(false);
        const error = ref(null);
        const maskedPhone = ref('');  // For displaying masked phone number
        
        // Color selection
        const selectedColor = ref({ colorId: 1, value: '#039BE5' }); // Default to Tomate
        const predefinedColors = [
            { colorId: 11, name: 'Tomate', value: '#D50000' },
            { colorId: 4, name: 'Flamingo', value: '#E67C73' },
            { colorId: 6, name: 'Tangerina', value: '#F4511E' },
            { colorId: 5, name: 'Banana', value: '#F6BF26' },
            { colorId: 2, name: 'Sálvia', value: '#33B679' },
            { colorId: 10, name: 'Manjericão', value: '#0B8043' },
            { colorId: 1, name: 'Pavão', value: '#039BE5' },
            { colorId: 9, name: 'Mirtilo', value: '#3F51B5' },
            { colorId: 7, name: 'Lavanda', value: '#7986CB' },
            { colorId: 3, name: 'Uva', value: '#8E24AA' },
            { colorId: 8, name: 'Grafite', value: '#616161' }
        ];
        
        // Working hours data
        const daysOfWeek = [
            { key: 'sunday', label: 'Domingo', short: 'DOM' },
            { key: 'monday', label: 'Segunda-Feira', short: 'SEG' },
            { key: 'tuesday', label: 'Terça-Feira', short: 'TER' },
            { key: 'wednesday', label: 'Quarta-Feira', short: 'QUA' },
            { key: 'thursday', label: 'Quinta-Feira', short: 'QUI' },
            { key: 'friday', label: 'Sexta-Feira', short: 'SEX' },
            { key: 'saturday', label: 'Sábado', short: 'SAB' }
        ];
        
        
        const workingHours = ref({});
        const showWorkingHours = ref(false);
        
        // Initialize working hours for each day
        daysOfWeek.forEach(day => {
            workingHours.value[day.key] = {
                enabled: day.key !== 'saturday' && day.key !== 'sunday',
                timeSlots: [
                    { startTime: '09:00', endTime: '17:00' }
                ]
            };
        });
        
        // Working hours errors
        const workingHoursErrors = ref({});
        
        // Specialty dropdown state
        const showSpecialtyDropdown = ref(false);
        const specialtyButton = ref(null);
        const dropdownStyle = ref({});
        
        // Color dropdown state
        const showColorDropdown = ref(false);
        const colorButton = ref(null);
        const colorDropdownStyle = ref({});

        // Computed properties
        const modalTitle = computed(() => {
            switch (props.mode) {
                case 'add': return 'Adicionar Profissional';
                case 'edt': return 'Editar Profissional';
                case 'del': return 'Excluir Profissional';
                default: return '';
            }
        });
        
        // Email validation
        const isEmailValid = computed(() => {
            const email = formData.value.email;
            if (!email || email.trim() === '') return true; // Empty is considered valid (optional field)
            
            const atIndex = email.indexOf('@');
            if (atIndex === -1) return false; // No @ symbol
            
            const afterAt = email.substring(atIndex + 1);
            const dotIndex = afterAt.indexOf('.');
            if (dotIndex === -1) return false; // No . after @
            
            // Check if there's text after the dot
            const afterDot = afterAt.substring(dotIndex + 1);
            if (afterDot.trim() === '') return false; // No text after the dot
            
            return true;
        });

        const actionButtonClass = computed(() => {
            const baseClass = "inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 outline-transparent h-10 px-4 text-sm font-medium justify-center";
            
            if (props.mode === 'del') {
                return `${baseClass} bg-n-ruby-9 text-white hover:enabled:bg-n-ruby-10 focus-visible:bg-n-ruby-10`;
            }
            return `${baseClass} bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110`;
        });

        const actionButtonText = computed(() => {
            if (loading.value) {
                switch (props.mode) {
                    case 'add': return 'Adicionando...';
                    case 'edt': return 'Salvando...';
                    case 'del': return 'Excluindo...';
                    default: return 'Processando...';
                }
            }
            
            switch (props.mode) {
                case 'add': return 'Adicionar profissional';
                case 'edt': return 'Salvar alterações';
                case 'del': return 'Excluir profissional';
                default: return '';
            }
        });

        const actionButtonDisabled = computed(() => {
            if (loading.value) return true;
            if (props.mode === 'del') return false;
            return !isFormValid.value;
        });

        const isFormValid = computed(() => {
            if (props.mode === 'del') return true;
            
            // Basic form validation - name and specialty are required
            const hasRequiredFields = formData.value.name && formData.value.name.trim() !== '' && selectedSpecialties.value.length > 0;
            
            // Email validation - if email is provided, it must be valid
            const emailValid = isEmailValid.value;
            
            // If email is invalid, form is invalid
            if (!emailValid) {
                return false;
            }
            
            // Working hours validation for add/edit modes (only if working hours section is expanded)
            if ((props.mode === 'add' || props.mode === 'edt') && showWorkingHours.value) {
                const hasAtLeastOneEnabledDay = Object.values(workingHours.value).some(day => day.enabled);
                
                if (!hasAtLeastOneEnabledDay) {
                    return false;
                }
                
                const allEnabledDaysValid = Object.keys(workingHours.value).every(dayKey => {
                    const day = workingHours.value[dayKey];
                    if (!day.enabled) return true;

                    // Check if all slots have filled times
                    const hasEmptySlots = day.timeSlots.some(slot => !slot.startTime || !slot.endTime);
                    if (hasEmptySlots) {
                        return false;
                    }

                    return day.timeSlots.every((_, index) => validateTimeSlot(dayKey, index));
                });
                
                return hasRequiredFields && allEnabledDaysValid;
            }
            
            return hasRequiredFields;
        });


        // Function to check if all form fields are empty
        const areAllFieldsEmpty = () => {
            return (
                formData.value.name.trim() === '' &&
                formData.value.email.trim() === '' &&
                formData.value.category.length === 0 &&
                selectedSpecialties.value.length === 0 &&
                customField.value.trim() === '' &&
                !showWorkingHours.value
            );
        };

        // Function to handle backdrop click
        const handleBackdropClick = () => {
            if (props.mode === 'del' || areAllFieldsEmpty()) {
                closeModal();
            }
        };

        const closeModal = () => {
            showSpecialtyDropdown.value = false;
            showColorDropdown.value = false;
            updateModalScrollLock(); // Reset scroll state
            emit('close');
        };
        
        // Color selection function
        const selectColor = (color) => {
            selectedColor.value = color;
        };
        
        // Color dropdown functions
        const toggleColorDropdown = () => {
            showColorDropdown.value = !showColorDropdown.value;
            if (showColorDropdown.value && colorButton.value) {
                const buttonRect = colorButton.value.getBoundingClientRect();
                colorDropdownStyle.value = {
                    left: buttonRect.left + 'px',
                    top: buttonRect.bottom + 2 + 'px',
                    width: buttonRect.width + 'px'
                };
            }
            updateModalScrollLock();
        };
        
        const closeColorDropdown = () => {
            showColorDropdown.value = false;
            updateModalScrollLock();
        };
        
        const selectColorFromDropdown = (color) => {
            selectColor(color);
            closeColorDropdown();
        };
        
        // Specialty dropdown functions
        const toggleSpecialtyDropdown = () => {
            showSpecialtyDropdown.value = !showSpecialtyDropdown.value;
            if (showSpecialtyDropdown.value && specialtyButton.value) {
                const buttonRect = specialtyButton.value.getBoundingClientRect();
                dropdownStyle.value = {
                    left: buttonRect.left + 'px',
                    top: buttonRect.bottom + 2 + 'px',
                    width: buttonRect.width + 'px'
                };
            }
            updateModalScrollLock();
        };
        
        const closeSpecialtyDropdown = () => {
            showSpecialtyDropdown.value = false;
            updateModalScrollLock();
        };
        
        const toggleSpecialty = (specialtyId) => {
            const index = selectedSpecialties.value.indexOf(specialtyId);
            if (index > -1) {
                selectedSpecialties.value.splice(index, 1);
            } else {
                selectedSpecialties.value.push(specialtyId);
            }
        };
        
        // Update modal scroll lock based on dropdown state
        const updateModalScrollLock = () => {
            const modalBody = document.querySelector('.p-6.overflow-auto.flex-1');
            if (modalBody) {
                if (showSpecialtyDropdown.value || showColorDropdown.value) {
                    modalBody.addEventListener('wheel', preventScroll, { passive: false });
                    modalBody.addEventListener('touchmove', preventScroll, { passive: false });
                } else {
                    modalBody.removeEventListener('wheel', preventScroll);
                    modalBody.removeEventListener('touchmove', preventScroll);
                }
            }
        };

        // Prevent scroll event
        const preventScroll = (e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
        };
        
        const getSelectedSpecialtiesText = () => {
            if (!selectedSpecialties.value.length || !specialties.value) return 'Nenhuma selecionada';
            
            const selectedNames = selectedSpecialties.value.map(id => {
                const specialty = specialties.value.find(s => s.id === id);
                return specialty ? specialty.name : '';
            }).filter(name => name);
            
            if (selectedNames.length === 0) return 'Nenhuma selecionada';
            if (selectedNames.length === 1) return selectedNames[0];
            if (selectedNames.length === 2) return `${selectedNames[0]} e ${selectedNames[1]}`;
            return `${selectedNames[0]} e mais ${selectedNames.length - 1}`;
        };

        // Transition group hooks for smooth card animations
        const beforeEnter = (el) => {
            // Start collapsed & invisible
            el.style.height = '0';
            el.style.opacity = '0';
        };

        const enter = (el, done) => {
            // Read full height by temporarily clearing height
            el.style.transition = '';
            el.style.height = 'auto';
            const fullHeight = el.offsetHeight + 'px';

            // Reset to zero and set up transition
            el.style.height = '0';
            el.style.opacity = '0';
            // Force repaint
            void el.offsetHeight;

            el.style.transition = 'height 0.3s ease, opacity 0.3s ease';
            el.style.height = fullHeight;
            el.style.opacity = '1';

            setTimeout(done, 300);
        };

        const afterEnter = (el) => {
            // Clean up
            el.style.transition = '';
            el.style.height = '';
            el.style.opacity = '';
        };

        const beforeLeave = (el) => {
            // Fix its starting height so we can collapse from there
            el.style.height = el.offsetHeight + 'px';
            el.style.opacity = '1';
        };

        const leave = (el, done) => {
            // Force a repaint so the starting height sticks
            void el.offsetHeight;
            el.style.transition = 'height 0.3s ease, opacity 0.3s ease';
            el.style.height = '0';
            el.style.opacity = '0';
            setTimeout(done, 300);
        };

        const afterLeave = (el) => {
            // Clean up inline styles
            el.style.transition = '';
            el.style.height = '';
            el.style.opacity = '';
        };


        // Auth functions
        const getCookie = (name) => {
            const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
            return match ? decodeURIComponent(match[2]) : null;
        };

        const getAuthHeaders = () => {
            const cookie = getCookie('cw_d_session_info');
            if (!cookie) {
                console.error('cw_d_session_info cookie not found');
                return {};
            }
            try {
                const sessionInfo = JSON.parse(cookie);
                return {
                    'access-token': sessionInfo['access-token'],
                    'client': sessionInfo['client'],
                    'uid': sessionInfo['uid']
                };
            } catch (e) {
                console.error('Error parsing cw_d_session_info cookie:', e);
                return {};
            }
        };

        // Action handlers
        const handleAction = () => {
            switch (props.mode) {
                case 'add':
                    saveProfessional();
                    break;
                case 'edt':
                    updateProfessional();
                    break;
                case 'del':
                    deleteProfessional();
                    break;
            }
        };

        const saveProfessional = debounce(async () => {
            if (!isFormValid.value) return;

            loading.value = true;
            error.value = null;

            const headers = getAuthHeaders();
            if (!headers['access-token']) {
                error.value = 'Usuário não autenticado.';
                loading.value = false;
                return;
            }

            const categoryNames = selectedSpecialties.value.map(specialtyId => {
                const specialty = specialties.value.find(s => s.id === specialtyId);
                return specialty ? specialty.name : null;
            }).filter(name => name);

            const professionalData = {
                name: formData.value.name.trim(),
            };

            // Only include category if it has values
            if (categoryNames.length > 0) {
                professionalData.category = categoryNames;
            }

            // Include additional fields, working hours, and color as meta_data object
            const metaData = {};
            
            // Add colorId to meta_data
            metaData.colorId = selectedColor.value.colorId;
            
            // Add email to meta_data
            if (formData.value.email && formData.value.email.trim() !== '') {
                metaData.email = formData.value.email.trim();
            }
            
            // Add whatsapp to meta_data
            if (formData.value.whatsapp && formData.value.whatsapp.trim() !== '') {
                metaData.whatsapp = formData.value.whatsapp.trim();
            }
            
            // Add custom field to meta_data
            if (customField.value.trim() !== '') {
                metaData.custom_field = customField.value.trim();
            }
            
            // Add working hours to meta_data (only if working hours section is expanded)
            if (showWorkingHours.value) {
                const workingHoursData = {};
                
                // Process working hours for each day
                daysOfWeek.forEach(day => {
                    workingHoursData[day.key] = {
                        enabled: workingHours.value[day.key].enabled,
                        timeSlots: workingHours.value[day.key].timeSlots.map(slot => ({
                            startTime: slot.startTime,
                            endTime: slot.endTime
                        }))
                    };
                });
                
                metaData.working_hours = JSON.stringify(workingHoursData);
            }
            
            // Only add meta_data if there's something to save
            if (Object.keys(metaData).length > 0) {
                professionalData.meta_data = metaData;
            }

            try {
                const response = await wootAPI.post(
                    `/api/v1/professionals`,
                    { professional: professionalData },
                    { headers }
                );

                const savedProfessional = response.data;
                emit('save', savedProfessional);
                resetForm();
                closeModal();
            } catch (err) {
                console.error('Failed to save professional', err);
                error.value = err.response?.data?.message || 'Erro interno do servidor. Tente novamente.';
            } finally {
                loading.value = false;
            }
        }, 300);

        const updateProfessional = debounce(async () => {
            if (!isFormValid.value) return;

            loading.value = true;
            error.value = null;

            const headers = getAuthHeaders();
            if (!headers['access-token']) {
                error.value = 'Usuário não autenticado.';
                loading.value = false;
                return;
            }

            const categoryNames = selectedSpecialties.value.map(specialtyId => {
                const specialty = specialties.value.find(s => s.id === specialtyId);
                return specialty ? specialty.name : null;
            }).filter(name => name);

            const updatedProfessional = {
                name: formData.value.name.trim(),
            };

            // Only include category if it has values
            if (categoryNames.length > 0) {
                updatedProfessional.category = categoryNames;
            }

            // Include additional fields, working hours, and color as meta_data object
            const metaData = {};
            
            // Add colorId to meta_data
            metaData.colorId = selectedColor.value.colorId;
            
            // Add email to meta_data
            if (formData.value.email && formData.value.email.trim() !== '') {
                metaData.email = formData.value.email.trim();
            }
            
            // Add whatsapp to meta_data
            if (formData.value.whatsapp && formData.value.whatsapp.trim() !== '') {
                metaData.whatsapp = formData.value.whatsapp.trim();
            }
            
            // Add custom field to meta_data
            if (customField.value.trim() !== '') {
                metaData.custom_field = customField.value.trim();
            }
            
            // Add working hours to meta_data (only if working hours section is expanded)
            if (showWorkingHours.value) {
                const workingHoursData = {};
                
                // Process working hours for each day
                daysOfWeek.forEach(day => {
                    workingHoursData[day.key] = {
                        enabled: workingHours.value[day.key].enabled,
                        timeSlots: workingHours.value[day.key].timeSlots.map(slot => ({
                            startTime: slot.startTime,
                            endTime: slot.endTime
                        }))
                    };
                });
                
                metaData.working_hours = JSON.stringify(workingHoursData);
            }
            
            // Only add meta_data if there's something to save
            if (Object.keys(metaData).length > 0) {
                updatedProfessional.meta_data = metaData;
            }

            try {
                await wootAPI.patch(
                    `/api/v1/professionals/${props.professional.id}`,
                    { professional: updatedProfessional },
                    { headers }
                );

                emit('update', updatedProfessional);
                closeModal();
            } catch (err) {
                console.error('Erro ao editar profissional', err);
                error.value = err.response?.data?.message || 'Erro interno do servidor. Tente novamente.';
            } finally {
                loading.value = false;
            }
        }, 300);

        const deleteProfessional = debounce(async () => {
            loading.value = true;
            error.value = null;

            const headers = getAuthHeaders();
            if (!headers['access-token']) {
                error.value = 'Usuário não autenticado.';
                loading.value = false;
                return;
            }

            try {
                await wootAPI.delete(
                    `/api/v1/professionals/${props.professional.id}`,
                    { headers }
                );

                emit('delete', props.professional);
                closeModal();
            } catch (err) {
                console.error('Erro ao excluir profissional', err);
                error.value = err.response?.data?.message || 'Erro interno do servidor. Tente novamente.';
            } finally {
                loading.value = false;
            }
        }, 300);

        const initializeSelectedSpecialties = () => {
            // For edit mode, find and select current specialties
            if (props.mode === 'edt' && formData.value.category.length > 0 && specialties.value) {
                selectedSpecialties.value = [];
                formData.value.category.forEach(category => {
                    const categoryName = typeof category === 'string' ? category : category.name;
                    const specialty = specialties.value.find(s => s.name === categoryName);
                    if (specialty && !selectedSpecialties.value.includes(specialty.id)) {
                        selectedSpecialties.value.push(specialty.id);
                    }
                });
            }
        };

        const resetForm = () => {
            formData.value = {
                name: '',
                email: '',
                whatsapp: '',
                category: [],
                gender: ''
            };
            selectedSpecialties.value = [];
            customField.value = '';
            maskedPhone.value = '';
            selectedColor.value = { colorId: 1, value: '#039BE5' }; // Reset to Tomate
            error.value = null;
            
            // Reset working hours
            daysOfWeek.forEach(day => {
                workingHours.value[day.key] = {
                    enabled: day.key !== 'saturday' && day.key !== 'sunday',
                    timeSlots: [
                        { startTime: '09:00', endTime: '17:00' }
                    ]
                };
            });
            workingHoursErrors.value = {};
            showWorkingHours.value = false;
        };
        
        // Working hours functions
        
        const addTimeSlot = (dayKey) => {
            const lastSlot = workingHours.value[dayKey].timeSlots.slice(-1)[0];
            
            if (!lastSlot) {
                workingHours.value[dayKey].timeSlots.push({
                    startTime: '09:00',
                    endTime: '10:00'
                });
                return;
            }
            
            const [lastEndHour] = lastSlot.endTime.split(':').map(Number);
            if (lastEndHour === 0) {
                return;
            }
            
            const lastSlotIndex = workingHours.value[dayKey].timeSlots.length - 1;
            if (!validateTimeSlot(dayKey, lastSlotIndex)) {
                return;
            }
            
            let newStartTime = lastSlot.endTime;
            let newEndTime = getNextHour(newStartTime);
            
            const [startHour] = newStartTime.split(':').map(Number);
            if (startHour === 23) {
                newEndTime = '00:00';
            }

            workingHours.value[dayKey].timeSlots.push({
                startTime: newStartTime,
                endTime: newEndTime
            });
        };

        const removeTimeSlot = (dayKey, index) => {
            if (workingHours.value[dayKey].timeSlots.length > 1) {
                workingHours.value[dayKey].timeSlots.splice(index, 1);
            }
        };

        const duplicateToOtherDays = (sourceDayKey) => {
            const sourceSlots = workingHours.value[sourceDayKey].timeSlots;

            daysOfWeek.forEach(day => {
                if (day.key !== sourceDayKey && workingHours.value[day.key].enabled) {
                    workingHours.value[day.key].timeSlots = sourceSlots.map(slot => ({
                        startTime: slot.startTime,
                        endTime: slot.endTime
                    }));
                }
            });
        };

        const getNextHour = (time) => {
            const [hours, minutes] = time.split(':').map(Number);
            let nextHour = hours + 1;
            
            if (nextHour > 23) {
                nextHour = 0;
            }
            
            return `${nextHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        };

        const toggleDay = (dayKey) => {
            workingHours.value[dayKey].enabled = !workingHours.value[dayKey].enabled;
            ensureTimeSlots(dayKey);
        };

        const ensureTimeSlots = (dayKey) => {
            if (workingHours.value[dayKey].enabled && 
                (!workingHours.value[dayKey].timeSlots || 
                 workingHours.value[dayKey].timeSlots.length === 0)) {
                workingHours.value[dayKey].timeSlots = [
                    { startTime: '09:00', endTime: '17:00' }
                ];
            }
        };

        const validateTimeSlot = (dayKey, slotIndex) => {
            const day = workingHours.value[dayKey];
            const slot = day.timeSlots[slotIndex];
            const slotErrors = {};

            if (!slot.startTime) {
                slotErrors.startTime = 'Horário de início é obrigatório';
            }
            if (!slot.endTime) {
                slotErrors.endTime = 'Horário de fim é obrigatório';
            }

            if (slot.startTime && slot.endTime) {
                const startMinutes = timeToMinutes(slot.startTime);
                const endMinutes = timeToMinutes(slot.endTime);
                
                if (startMinutes >= endMinutes && endMinutes !== 0) {
                    slotErrors.endTime = 'Horário de fim deve ser após o início';
                } else if (startMinutes === endMinutes) {
                    slotErrors.endTime = 'Horário de fim deve ser diferente do início';
                }
            }

            day.timeSlots.forEach((otherSlot, otherIndex) => {
                if (otherIndex < slotIndex && slot.startTime && slot.endTime && otherSlot.startTime && otherSlot.endTime) {
                    const slotStart = timeToMinutes(slot.startTime);
                    const slotEnd = timeToMinutes(slot.endTime) || 1440;
                    const otherStart = timeToMinutes(otherSlot.startTime);
                    const otherEnd = timeToMinutes(otherSlot.endTime) || 1440;

                    if ((slotStart < otherEnd && slotEnd > otherStart)) {
                        slotErrors.overlap = 'Horários não podem se sobrepor';
                    }
                }
            });

            if (!workingHoursErrors.value[dayKey]) {
                workingHoursErrors.value[dayKey] = {};
            }
            workingHoursErrors.value[dayKey][slotIndex] = slotErrors;

            return Object.keys(slotErrors).length === 0;
        };

        const timeToMinutes = (time) => {
            const [hours, minutes] = time.split(':').map(Number);
            return hours * 60 + minutes;
        };

        
        const toggleWorkingHours = () => {
            showWorkingHours.value = !showWorkingHours.value;
        };

        // Phone number formatting functions
        const formatPhoneToSave = (phone) => {
            // Remove all non-digits
            const digitsOnly = phone.replace(/\D/g, '');
            // Add + at the beginning
            return digitsOnly ? '+' + digitsOnly : '';
        };

        const formatPhoneToDisplay = (phone) => {
            if (!phone) return '';
            // Remove + if present, then format
            const cleanPhone = phone.replace(/^\+/, '').replace(/\D/g, '');
            if (!cleanPhone) return '';
            
            let formatted = '+';
            if (cleanPhone.length > 0) formatted += cleanPhone.substring(0, 2);
            if (cleanPhone.length > 2) formatted += ' ' + cleanPhone.substring(2, 4);
            if (cleanPhone.length > 4) formatted += ' ' + cleanPhone.substring(4, 9);
            if (cleanPhone.length > 9) formatted += '-' + cleanPhone.substring(9, 13);
            
            return formatted;
        };

        const handlePhoneInput = (event) => {
            const input = event.target.value;
            const digitsOnly = input.replace(/\D/g, '');
            
            // Update the raw phone value
            formData.value.whatsapp = formatPhoneToSave(digitsOnly);
            
            // Update the masked display
            maskedPhone.value = formatPhoneToDisplay(digitsOnly);
        };

        const onlyNumbers = (event) => {
            const char = String.fromCharCode(event.keyCode || event.which);
            if (!/[0-9]/.test(char) && event.keyCode !== 8 && event.keyCode !== 46) {
                event.preventDefault();
            }
        };

        // Watchers
        watch([
            () => formData.value.name,
            () => formData.value.email,
            () => formData.value.category,
        ], () => { error.value = null; });

        watch(selectedSpecialties, newIds => {
            if (!specialties.value) return;
            formData.value.category = newIds.map(id => {
                const specialty = specialties.value.find(s => s.id === id);
                return specialty ? specialty.name : null;
            }).filter(name => name);
        }, { deep: true });

        // Cleanup function for scroll listeners
        const cleanupScrollListeners = () => {
            const modalBody = document.querySelector('.p-6.overflow-auto.flex-1');
            if (modalBody) {
                modalBody.removeEventListener('wheel', preventScroll);
                modalBody.removeEventListener('touchmove', preventScroll);
            }
        };
        
        onMounted(() => {
            // Initialize form data for edit mode
            if (props.mode === 'edt' && props.professional) {
                formData.value = {
                    name: props.professional.name || '',
                    email: '',
                    whatsapp: '',
                    category: props.professional.category ?
                        props.professional.category.map(cat => typeof cat === 'string' ? { name: cat } : cat) :
                        [],
                    gender: props.professional.gender || ''
                };

                // Initialize additional fields, working hours, and color from meta_data for edit mode
                if (props.professional.meta_data && typeof props.professional.meta_data === 'object') {
                    // Handle colorId from meta_data
                    if (props.professional.meta_data.colorId) {
                        const colorFound = predefinedColors.find(c => c.colorId === props.professional.meta_data.colorId);
                        if (colorFound) {
                            selectedColor.value = colorFound;
                        }
                    }
                    
                    // Handle email from meta_data
                    if (props.professional.meta_data.email) {
                        formData.value.email = props.professional.meta_data.email;
                    }
                    
                    // Handle whatsapp from meta_data
                    if (props.professional.meta_data.whatsapp) {
                        formData.value.whatsapp = props.professional.meta_data.whatsapp;
                        maskedPhone.value = formatPhoneToDisplay(props.professional.meta_data.whatsapp.replace(/^\+/, ''));
                    }
                    
                    // Handle working hours from meta_data
                    if (props.professional.meta_data.working_hours) {
                        try {
                            const workingHoursData = JSON.parse(props.professional.meta_data.working_hours);
                            
                            // Load working hours for each day
                            // Check if it's the old nested format or the new flat format
                            const hoursData = workingHoursData.workingHours || workingHoursData;
                            
                            daysOfWeek.forEach(day => {
                                if (hoursData[day.key]) {
                                    workingHours.value[day.key] = {
                                        enabled: hoursData[day.key].enabled,
                                        timeSlots: hoursData[day.key].timeSlots.map(slot => ({
                                            startTime: slot.startTime,
                                            endTime: slot.endTime
                                        }))
                                    };
                                }
                            });
                            
                            // Show working hours section since data exists
                            showWorkingHours.value = true;
                        } catch (e) {
                            console.error('Error parsing working hours from meta_data:', e);
                        }
                    }
                    
                    // Handle custom field
                    if (props.professional.meta_data.custom_field) {
                        customField.value = props.professional.meta_data.custom_field;
                    }
                }
            }

            // Initialize selected specialties for edit mode
            if (props.mode === 'edt') {
                initializeSelectedSpecialties();
            }
        });
        
        onBeforeUnmount(() => {
            cleanupScrollListeners();
        });

        return {
            formData,
            isFormValid,
            isEmailValid,
            closeModal,
            loading,
            error,
            specialties,
            selectedSpecialties,
            handleBackdropClick,
            areAllFieldsEmpty,
            modalTitle,
            actionButtonClass,
            actionButtonText,
            actionButtonDisabled,
            handleAction,
            // phone number
            maskedPhone,
            handlePhoneInput,
            onlyNumbers,
            // color selection
            selectedColor,
            predefinedColors,
            selectColor,
            // color dropdown
            showColorDropdown,
            colorButton,
            colorDropdownStyle,
            toggleColorDropdown,
            closeColorDropdown,
            selectColorFromDropdown,
            // specialty dropdown
            showSpecialtyDropdown,
            toggleSpecialtyDropdown,
            closeSpecialtyDropdown,
            toggleSpecialty,
            getSelectedSpecialtiesText,
            specialtyButton,
            dropdownStyle,
            // custom field
            customField,
            // transition hooks
            beforeEnter,
            enter,
            afterEnter,
            beforeLeave,
            leave,
            afterLeave,
            // working hours
            daysOfWeek,
            workingHours,
            workingHoursErrors,
            showWorkingHours,
            toggleWorkingHours,
            addTimeSlot,
            removeTimeSlot,
            duplicateToOtherDays,
            toggleDay,
            ensureTimeSlots,
            validateTimeSlot,
            // scroll lock
            updateModalScrollLock,
            preventScroll,
            cleanupScrollListeners
        };
    }
}
</script>

<style scoped>
/* Minimal custom styles - most styling now handled by Tailwind utilities */

/* Screen reader only class for accessibility */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Transition for smooth sliding of additional fields */
.slide-move {
  transition: transform 0.3s ease;
}

</style>